import { lazy, Suspense, useState, useEffect } from 'react';

import { IconEdit } from '@tabler/icons-react';

import { Breadcrumb } from '@/components/BreadCrumbs';
import CustomModal from '@/components/CustomModal';
import UserDetailsSkeleton from '@/components/SkeletonLoader/UserDetailsSkeleton';
import { ERoleAccess, ESubscriberType } from '@/enum';
import useAccess from '@/hooks/useAccess';
import { useFormatText } from '@/hooks/useFormatText';
import { updateChildLevel, getStudentDetails } from '@/service/childrenService';
import useCommonStore from '@/store/useCommonStore';
import { ISelectedRowValue, ISubscriptionChildDetails } from '@/types';

import { SchoolDetailsContent } from './SchoolDetailsContent';

// Lazy-load heavy modal children to reduce initial bundle size
const ChildDetailsContent = lazy(() => import('./ChildDetailsContent'));
const ChildEditDetailsContent = lazy(() => import('./ChildEditDetailsContent'));
const ParentDetailsContent = lazy(() => import('./ParentDetailsContent'));

type TView = 'parent' | 'child' | 'editChild' | 'school';

interface IParentDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  selectedChildId: string | undefined;
  subscriberType: ESubscriberType;
  onEditSave?: () => void;
  isOpenBlockModal: boolean;
  setIsOpenBlockModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleBlockChildren: (child?: ISubscriptionChildDetails) => void;
  setSelectedChildren: (value: ISelectedRowValue | null) => void;
}

const ChildParentDetailsModal = ({
  setIsOpen,
  selectedChildId,
  subscriberType,
  onEditSave,
  isOpenBlockModal,
  setIsOpenBlockModal,
  handleBlockChildren,
  setSelectedChildren,
}: IParentDetailsModalProps) => {
  const { hasAccess } = useAccess();

  const { loadingModal, setLoadingModal } = useCommonStore();
  const { formatText } = useFormatText();
  const [view, setView] = useState<TView>('child');
  const [internalSelectedChildId, setInternalSelectedChildId] = useState<
    string | null
  >(null);
  const [schoolIdToOpen, setSchoolIdToOpen] = useState<string | undefined>();
  const [parentId, setParentId] = useState<string | undefined>();
  const [child, setChild] = useState<ISubscriptionChildDetails | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLevel, setPendingLevel] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (selectedChildId) {
      setInternalSelectedChildId(selectedChildId);
      setView('child');
    }
  }, [selectedChildId]);

  // Reset unsaved changes when switching views
  useEffect(() => {
    if (view !== 'editChild') {
      setHasUnsavedChanges(false);
      setPendingLevel('');
    }
  }, [view]);

  const getBreadcrumbPath = () => [
    {
      label: formatText('parent.child-details'), // Translated "Child Details"
      href: 'child',
    },
    {
      label: formatText('parent.parent-details'), // Translated "Parent Details"
      href: 'parent',
    },
  ];

  const handleChildClick = (childId: string) => {
    setInternalSelectedChildId(childId);
    setView('child');
  };

  const handleParentClick = (id?: string) => {
    if (id) {
      setParentId(id);
      setView('parent');
    }
  };

  const handleOpenSchoolModal = (schoolId?: string) => {
    // Allow parent-provided callback to run if present, then open internal modal
    if (schoolId) {
      setSchoolIdToOpen(schoolId);
      setView('school');
    } else {
      setSchoolIdToOpen(undefined);
    }
  };

  const handleClose = () => {
    setView('child'); // Reset to default on close
    setInternalSelectedChildId(null);
    setIsOpen(false);
  };

  const handleHasChanges = (hasChanges: boolean) => {
    setHasUnsavedChanges(hasChanges);
  };

  const handleSaveLevel = async (level: string): Promise<boolean> => {
    setPendingLevel(level);
    return true; // Just store the level for now, actual save happens on button click
  };

  const handleSaveChanges = async () => {
    if (!internalSelectedChildId || !child?.id) return;

    // Get the pending level from child component
    const levelToSave = pendingLevel || child.learning_stage;
    if (!levelToSave) return;

    setLoadingModal(true);
    setIsSaving(true);
    try {
      // Call API to update the learning level
      const updateResponse = await updateChildLevel({
        children_id: child?.id,
        stage: String(levelToSave),
      });

      if (updateResponse?.status) {
        onEditSave?.(); // refresh list if callback provided

        // Refetch child details to ensure we have the latest data
        const response = await getStudentDetails(internalSelectedChildId);
        if (response?.status) {
          setChild(response?.data);
          setPendingLevel('');
          setHasUnsavedChanges(false);
          setView('child');
        } else {
          console.error(response?.message);
        }
      } else {
        console.error(updateResponse?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
      setLoadingModal(false);
    }
  };

  const headerContent =
    view === 'child' ? (
      <div className="flex justify-between items-center w-full">
        <h2 className="text-neutral-primary font-medium leading-7 text-lg">
          {formatText('parent.child-details')}
        </h2>
        <IconEdit
          size={16}
          color="#717784"
          onClick={() => setView('editChild')}
          className="hover:bg-white  text-neutral-pressed  w-8 h-8 p-1.5 rounded-sm bg-neutral-light-50 cursor-pointer me-4"
        />
      </div>
    ) : view === 'editChild' ? (
      <h2 className="text-neutral-primary font-medium leading-7 text-lg">
        {formatText('children.details.edit-child-profile')}
      </h2>
    ) : view === 'school' ? (
      <h2 className="text-neutral-primary font-medium leading-7 text-lg">
        {formatText('children.details.school-details')}
      </h2>
    ) : (
      <Breadcrumb
        path={getBreadcrumbPath()}
        onNavigateRequest={(href) => {
          if (href === 'child') {
            setView('child');
          }
        }}
      />
    );

  const confirmButtonProps = {
    label:
      view === 'editChild' ? formatText('common.actions.save-changes') : '',
    variant: 'primary' as const,
    className: view === 'editChild' ? 'ms-auto' : '',
    onClick: view === 'editChild' ? handleSaveChanges : () => setIsOpen(false),
    disabled: view === 'editChild' ? isSaving || !hasUnsavedChanges : true,
  };

  if (!hasAccess(ERoleAccess.USER_MANAGEMENT)) return null;

  return (
    <CustomModal
      setIsOpen={handleClose}
      isHeader
      position="right"
      headercontent={headerContent}
      isFooter={view === 'editChild' || view === 'school'}
      confirmButtonProps={confirmButtonProps}
      loadingModal={loadingModal}
    >
      <Suspense fallback={<UserDetailsSkeleton />}>
        {view === 'child' ? (
          <ChildDetailsContent
            child={child}
            setChild={setChild}
            selectedChildId={internalSelectedChildId}
            onParentClick={handleParentClick}
            subscriberType={subscriberType}
            onSchoolClick={handleOpenSchoolModal}
            isOpenBlockModal={isOpenBlockModal}
            setIsOpenBlockModal={setIsOpenBlockModal}
            handleBlockChildren={handleBlockChildren}
            setSelectedChildren={setSelectedChildren}
            setIsOpen={setIsOpen}
          />
        ) : view === 'editChild' ? (
          <ChildEditDetailsContent
            child={child}
            onSave={handleSaveLevel}
            onHasChanges={handleHasChanges}
            subscriberType={subscriberType}
          />
        ) : view === 'school' ? (
          <SchoolDetailsContent schoolId={schoolIdToOpen} />
        ) : (
          <ParentDetailsContent
            parentId={parentId}
            onChildClick={handleChildClick}
          />
        )}
      </Suspense>
    </CustomModal>
  );
};

export default ChildParentDetailsModal;
